import type { NASensorMapObject, NANevataSensorObject, NAMTObject } from '@/api/models';
import { NAMTMessageType } from '@/types/enums';
import { useFieldPending } from '@/hooks';
import { resolveOnlineState } from '@/lib/enum-resolvers';
import { SectionCard, SettingRow, SelectDropdown, EditableNameField, PendingBadge } from '@/components/common/ConfigControls';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { Button } from '@/components/common/Button/Button';
import {
  getCombinedAddress,
  findPendingMT,
  cascadeFromBilgeLevel,
  cascadeFromSirenLevel,
  cascadeFromCriticalLevel,
  pumpTurnOnLevelOptions,
  highWaterLevelOptions,
  criticalWaterLevelOptions,
  type NevataLevels,
} from '@/lib/sensor-commands';

const NUM_PUMPS_OVERRIDE_UNSET = 0xff;

// Mirrors NevataSettingsPageViewModel.cs — this drill-down shows only the
// configurable settings mobile exposes on the Nevata settings page (name, num
// pumps, auto calibrate, operating levels). No telemetry/status visualization —
// that lives in the read-only Bilge & Float Switch panel (NevataStatusDisplay).
export function NevataSensorConfig({
  sensor,
  nev,
  mtObjList,
  isExpanded,
  onToggleExpand,
  sendCommand,
  onUnpairRequest,
}: {
  sensor: NASensorMapObject;
  nev?: NANevataSensorObject;
  mtObjList: NAMTObject[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  sendCommand: (cmd: NAMTMessageType, dataStrList?: string[]) => Promise<boolean>;
  onUnpairRequest: (target: { msb: number; lsb: number; name: string }) => void;
}) {
  const fieldPending = useFieldPending();
  const combinedAddress = getCombinedAddress(sensor.sourceAddressMSB, sensor.sourceAddressLSB);
  const matchAddr = (d: string[]) => d[0] === combinedAddress;
  const isOffline = resolveOnlineState(nev?.onlineState) === 'Offline';

  const refreshSettings = () => sendCommand(NAMTMessageType.NEVATA_GET_ADVANCED_SETTINGS, [combinedAddress]);

  const sendLevels = async (levels: NevataLevels) => {
    await sendCommand(NAMTMessageType.NEVATA_SET_NEVATA_PARAMS, [
      combinedAddress,
      String(levels.bilgeLevel),
      String(levels.sirenLevel),
      String(levels.criticalLevel),
    ]);
    await refreshSettings();
  };

  const pendingName = findPendingMT(mtObjList, NAMTMessageType.SET_SENSOR_NAME, matchAddr);
  const pendingUnpair = findPendingMT(mtObjList, NAMTMessageType.UNPAIR_SENSOR, matchAddr);
  const pendingNumPumps = findPendingMT(mtObjList, NAMTMessageType.NEVATA_SET_NUM_PUMPS_CONTROLLED, matchAddr);
  const pendingAutoCalNormal = findPendingMT(mtObjList, NAMTMessageType.NEVATA_AUTO_CALIBRATE, matchAddr)
    ?? findPendingMT(mtObjList, NAMTMessageType.NEVATA_FORCE_CALIBRATE_NORMAL_OPERATION, matchAddr);
  const pendingAutoCalMonitoring = findPendingMT(mtObjList, NAMTMessageType.NEVATA_FORCE_CALIBRATE_MONITORING_MODE, matchAddr);
  const pendingAutoCalibrate = pendingAutoCalNormal ?? pendingAutoCalMonitoring;
  const pendingLevels = findPendingMT(mtObjList, NAMTMessageType.NEVATA_SET_NEVATA_PARAMS, matchAddr);

  // All three level dropdowns cascade together (one send updates all three), so
  // an optimistic edit to any one of them must be reflected across all three
  // rows immediately — whichever field the user just touched holds the new
  // combined snapshot.
  const optimisticLevels =
    fieldPending.getOptimisticValue<NevataLevels>('bilgeLevel') ??
    fieldPending.getOptimisticValue<NevataLevels>('sirenLevel') ??
    fieldPending.getOptimisticValue<NevataLevels>('criticalLevel');

  const currentLevels: NevataLevels =
    optimisticLevels ??
    (pendingLevels
      ? {
          bilgeLevel: Number(pendingLevels.dataStrList?.[1] ?? 0),
          sirenLevel: Number(pendingLevels.dataStrList?.[2] ?? 0),
          criticalLevel: Number(pendingLevels.dataStrList?.[3] ?? 0),
        }
      : {
          bilgeLevel: nev?.bilgeLevel ?? 0,
          sirenLevel: nev?.sirenLevel ?? 0,
          criticalLevel: nev?.criticalLevel ?? 0,
        });
  const calibrationOffset = nev?.calibrationOffset ?? 0;

  const showAutoCalibrate = nev && nev.numPumpsOverride !== NUM_PUMPS_OVERRIDE_UNSET;
  const showOperatingValues = nev?.isCalibrated === true && calibrationOffset > 0;

  const numPumpsValue =
    fieldPending.getOptimisticValue<string>('numPumps') ?? pendingNumPumps?.dataStrList?.[1] ?? String(nev?.numPumps ?? 1);
  const autoCalibrateValue =
    fieldPending.getOptimisticValue<string>('autoCalibrate') ??
    (pendingAutoCalibrate ? (pendingAutoCalMonitoring ? '2' : '1') : nev?.monitoringOnly ? '2' : '1');

  return (
    <div className="rounded-lg border border-slate-200 dark:border-navy-700">
      {/* Sensor header row */}
      <div
        role="button"
        tabIndex={0}
        onClick={onToggleExpand}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onToggleExpand();
        }}
        className="flex w-full cursor-pointer items-center justify-between px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-navy-800"
      >
        <div className="group flex items-center">
          <EditableNameField
            value={fieldPending.getOptimisticValue<string>('name') ?? pendingName?.dataStrList?.[1] ?? sensor.sensorName}
            pending={fieldPending.isPending('name') || !!pendingName}
            onSave={(newName) =>
              fieldPending.run('name', newName, () => sendCommand(NAMTMessageType.SET_SENSOR_NAME, [combinedAddress, newName]))
            }
          />
          <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">
            Addr: {sensor.sourceAddressMSB}:{sensor.sourceAddressLSB}
          </span>
          {isOffline && (
            <span className="ml-2">
              <StatusBadge label="Offline" variant="danger" dot />
            </span>
          )}
          {pendingUnpair && <span className="ml-2 text-xs font-medium text-red-500">Deleting…</span>}
        </div>
        <svg
          className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isExpanded && !pendingUnpair && (
        <div className="space-y-4 border-t border-slate-200 bg-slate-50 px-4 py-4 dark:border-navy-700 dark:bg-navy-800/50">
          <SectionCard title="Settings">
            <div className="divide-y divide-slate-100 dark:divide-navy-800">
              <SettingRow label="Num Pumps Controlled" description="How many pumps this sensor drives.">
                <div className="flex items-center">
                  <SelectDropdown
                    value={numPumpsValue}
                    options={[
                      { value: '1', label: '1' },
                      { value: '2', label: '2' },
                    ]}
                    disabled={fieldPending.isPending('numPumps')}
                    onChange={(val) =>
                      fieldPending.run('numPumps', val, async () => {
                        await sendCommand(NAMTMessageType.NEVATA_SET_NUM_PUMPS_CONTROLLED, [combinedAddress, val]);
                        await refreshSettings();
                      })
                    }
                  />
                  {(fieldPending.isPending('numPumps') || pendingNumPumps) && <PendingBadge />}
                </div>
              </SettingRow>

              {showAutoCalibrate && (
                <SettingRow label="Auto Calibrate" description="Recalibrate this sensor's operating range.">
                  <div className="flex items-center">
                    <SelectDropdown
                      value={autoCalibrateValue}
                      options={[
                        { value: '1', label: 'Normal Operation' },
                        { value: '2', label: 'Monitoring Only' },
                      ]}
                      disabled={fieldPending.isPending('autoCalibrate')}
                      onChange={(val) =>
                        fieldPending.run('autoCalibrate', val, async () => {
                          await sendCommand(NAMTMessageType.NEVATA_DISABLE_IGNORE_ACOUSTIC_SIGNAL_LOSS, [combinedAddress]);
                          if (val === '1') {
                            if (nev?.monitoringOnly) {
                              await sendCommand(NAMTMessageType.NEVATA_FORCE_CALIBRATE_NORMAL_OPERATION, [
                                combinedAddress,
                                String(calibrationOffset),
                              ]);
                            } else {
                              await sendCommand(NAMTMessageType.NEVATA_AUTO_CALIBRATE, [combinedAddress]);
                            }
                          } else {
                            await sendCommand(NAMTMessageType.NEVATA_FORCE_CALIBRATE_MONITORING_MODE, [combinedAddress]);
                          }
                        })
                      }
                    />
                    {(fieldPending.isPending('autoCalibrate') || pendingAutoCalibrate) && <PendingBadge />}
                  </div>
                </SettingRow>
              )}
            </div>
          </SectionCard>

          {showOperatingValues && (
            <SectionCard title="Operating Values">
              <div className="divide-y divide-slate-100 dark:divide-navy-800">
                {!nev?.monitoringOnly && (
                  <SettingRow label="Pump Turn On Level" description="Water level that starts the pump.">
                    <div className="flex items-center">
                      <SelectDropdown
                        value={String(currentLevels.bilgeLevel)}
                        options={pumpTurnOnLevelOptions(calibrationOffset).map((o) => ({ value: String(o.value), label: o.label }))}
                        disabled={fieldPending.isPending('bilgeLevel')}
                        onChange={(val) => {
                          const levels = cascadeFromBilgeLevel(Number(val), currentLevels, calibrationOffset);
                          fieldPending.run('bilgeLevel', levels, () => sendLevels(levels));
                        }}
                      />
                      {(fieldPending.isPending('bilgeLevel') || pendingLevels) && <PendingBadge />}
                    </div>
                  </SettingRow>
                )}
                <SettingRow label="High Water Level" description="Water level that triggers the siren alert.">
                  <div className="flex items-center">
                    <SelectDropdown
                      value={String(currentLevels.sirenLevel)}
                      options={highWaterLevelOptions(calibrationOffset).map((o) => ({ value: String(o.value), label: o.label }))}
                      disabled={fieldPending.isPending('sirenLevel')}
                      onChange={(val) => {
                        const levels = cascadeFromSirenLevel(Number(val), currentLevels, calibrationOffset);
                        fieldPending.run('sirenLevel', levels, () => sendLevels(levels));
                      }}
                    />
                    {(fieldPending.isPending('sirenLevel') || pendingLevels) && <PendingBadge />}
                  </div>
                </SettingRow>
                <SettingRow label="Critical Water Level" description="Water level that triggers a critical alert.">
                  <div className="flex items-center">
                    <SelectDropdown
                      value={String(currentLevels.criticalLevel)}
                      options={criticalWaterLevelOptions(calibrationOffset).map((o) => ({ value: String(o.value), label: o.label }))}
                      disabled={fieldPending.isPending('criticalLevel')}
                      onChange={(val) => {
                        const levels = cascadeFromCriticalLevel(Number(val), currentLevels);
                        fieldPending.run('criticalLevel', levels, () => sendLevels(levels));
                      }}
                    />
                    {(fieldPending.isPending('criticalLevel') || pendingLevels) && <PendingBadge />}
                  </div>
                </SettingRow>
              </div>
            </SectionCard>
          )}

          <Button
            size="sm"
            variant="danger"
            onClick={() =>
              onUnpairRequest({
                msb: sensor.sourceAddressMSB,
                lsb: sensor.sourceAddressLSB,
                name: sensor.sensorName,
              })
            }
          >
            Unpair
          </Button>
        </div>
      )}
    </div>
  );
}
